import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, MapPin, Users, Calendar, DollarSign,
  Ship, Map, Compass, PartyPopper, Car, Hotel, Loader2, Send, Sparkles,
  Camera, X, Check, StickyNote,
} from 'lucide-react';

const CATEGORY_ICONS = {
  yacht: Ship, tour: Map, activity: Compass, party: PartyPopper,
  car_rental: Car, lodging: Hotel,
  yachts: Ship, tours: Map, activities: Compass, nightlife: PartyPopper,
};

export default function ProviderRequestsPage() {
  const { api, token } = useAuth();
  const { language } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [form, setForm] = useState({ price: '', description: '', includes: '', note: '', images: [] });

  const fetchData = useCallback(async () => {
    try {
      const [r, o] = await Promise.all([
        api().get('/provider/requests'),
        api().get('/provider/offers'),
      ]);
      setRequests(r.data || []);
      setMyOffers(o.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Compress image client-side to Blob — max 1024px wide, JPEG quality 0.7 (small + fast)
  const compressToBlob = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        let { width, height } = img;
        if (width > MAX) {
          height = Math.round(height * (MAX / width));
          width = MAX;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('blob failed'))),
          'image/jpeg',
          0.7
        );
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const slots = Math.max(0, 3 - form.images.length);
    const queue = files.slice(0, slots);
    setUploading(true);
    setUploadProgress({ current: 0, total: queue.length });
    try {
      for (let i = 0; i < queue.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await compressToBlob(queue[i]);
        const fd = new FormData();
        fd.append('files', blob, `photo-${Date.now()}-${i}.jpg`);
        // eslint-disable-next-line no-await-in-loop
        const resp = await api().post('/upload/offer-images', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        const path = resp.data?.paths?.[0];
        if (path) {
          setForm((prev) => ({ ...prev, images: [...prev.images, path] }));
        }
        setUploadProgress({ current: i + 1, total: queue.length });
      }
    } catch (err) {
      console.error('upload error', err);
      toast.error(language === 'en' ? 'Photo upload failed. Try again.' : 'Falló la subida de foto. Reintenta.');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (e.target) e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const openOffer = (req) => {
    setSelectedRequest(req);
    setForm({ price: '', description: '', includes: '', note: '', images: [] });
    setIsOpen(true);
  };

  const submitOffer = async () => {
    if (!form.price || !form.description) {
      toast.error(language === 'en' ? 'Fill all required fields' : 'Completa los campos requeridos');
      return;
    }
    if (uploading) {
      toast.error(language === 'en' ? 'Photos are still uploading…' : 'Aún se están subiendo las fotos…');
      return;
    }
    setSubmitting(true);
    try {
      // Tiny payload now: only path strings, no base64
      await api().post('/provider/offers', {
        request_id: selectedRequest.id,
        price: parseFloat(form.price),
        description: form.description,
        includes: form.includes ? form.includes.split('\n').map((s) => s.trim()).filter(Boolean) : [],
        note: form.note?.trim() || null,
        images: form.images,
      });
      toast.success(language === 'en' ? 'Offer sent!' : '¡Oferta enviada!');
      setIsOpen(false);
      fetchData();
    } catch (e) {
      console.error('submitOffer error', e);
      const detail = e?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail.map((d) => d.msg || JSON.stringify(d)).join(', ');
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (e?.code === 'ECONNABORTED') {
        msg = language === 'en' ? 'Network timeout. Try again.' : 'Tiempo de red agotado. Reintenta.';
      } else {
        msg = e?.message || (language === 'en' ? 'Unknown error' : 'Error desconocido');
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const includesPreview = form.includes
    ? form.includes.split('\n').map((s) => s.trim()).filter(Boolean)
    : [];

  // Resolve photo URL — base64 dataURL or storage path
  const photoSrc = (p) => {
    if (!p) return null;
    if (typeof p === 'string' && p.startsWith('data:')) return p;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${token}`;
  };

  const formatBudget = (req) => {
    if (req.budget_min || req.budget_max) {
      return `$${req.budget_min || 0} - $${req.budget_max || 0}`;
    }
    if (req.suggested_avg) return `~$${req.suggested_avg}`;
    return language === 'en' ? 'Open budget' : 'Presupuesto abierto';
  };

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Open searches' : 'Solicitudes abiertas'}
        right={
          <Link to="/dashboard" className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ArrowLeft size={18} />
          </Link>
        }
      />

      <div className="px-4 py-4" data-testid="provider-requests-page">
        <Tabs defaultValue="open">
          <TabsList className="w-full grid grid-cols-2 rounded-full bg-muted h-10 mb-4">
            <TabsTrigger value="open" data-testid="provider-tab-open" className="rounded-full text-xs">
              {language === 'en' ? 'Open' : 'Abiertas'} ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="mine" data-testid="provider-tab-mine" className="rounded-full text-xs">
              {language === 'en' ? 'My offers' : 'Mis ofertas'} ({myOffers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="open" className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="animate-spin text-caribbean" /></div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border rounded-2xl">
                <Sparkles className="mx-auto text-caribbean mb-2" />
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'No open searches right now' : 'Sin solicitudes abiertas ahora'}
                </p>
              </div>
            ) : (
              requests.map((req) => {
                const Icon = CATEGORY_ICONS[req.category] || Compass;
                return (
                  <div key={req.id} data-testid={`req-card-${req.id}`} className="bg-card border border-border rounded-2xl p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-xl bg-caribbean/10 flex items-center justify-center shrink-0">
                        <Icon size={20} className="text-caribbean" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm capitalize">{req.category}</p>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                          <span className="inline-flex items-center gap-0.5"><MapPin size={11} /> {req.location?.replaceAll('_', ' ')}</span>
                          <span className="inline-flex items-center gap-0.5"><Calendar size={11} /> {req.date}</span>
                          <span className="inline-flex items-center gap-0.5"><Users size={11} /> {req.people_count}</span>
                        </p>
                        <div className="mt-1.5 flex items-center justify-between">
                          <span className="text-xs font-semibold text-caribbean inline-flex items-center gap-1">
                            <DollarSign size={11} /> {formatBudget(req)}
                          </span>
                          <Badge variant="outline" className="text-[10px]">
                            {req.offer_count || 0} {language === 'en' ? 'offers' : 'ofertas'}
                          </Badge>
                        </div>
                        {req.notes && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{req.notes}</p>
                        )}
                        <Button
                          data-testid={`make-offer-${req.id}`}
                          onClick={() => openOffer(req)}
                          size="sm"
                          className="w-full mt-2.5 h-8 rounded-full bg-caribbean hover:bg-caribbean/90 text-white text-xs"
                        >
                          <Send size={12} className="mr-1" />
                          {language === 'en' ? 'Make an offer' : 'Hacer una oferta'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="mine" className="space-y-3">
            {myOffers.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border rounded-2xl">
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'You have not made any offer yet' : 'Aún no has enviado ofertas'}
                </p>
              </div>
            ) : (
              myOffers.map((o) => (
                <div key={o.id} data-testid={`my-offer-${o.id}`} className="bg-card border border-border rounded-2xl p-3.5">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">
                      ${o.price} <span className="text-xs text-muted-foreground font-normal">USD</span>
                    </p>
                    <Badge className={
                      o.status === 'accepted' ? 'bg-caribbean text-white' :
                      o.status === 'rejected' ? 'bg-destructive text-white' :
                      'bg-gold text-foreground'
                    }>
                      {o.status === 'accepted' && (language === 'en' ? 'Accepted' : 'Aceptada')}
                      {o.status === 'rejected' && (language === 'en' ? 'Rejected' : 'Rechazada')}
                      {(!o.status || o.status === 'pending') && (language === 'en' ? 'Pending' : 'Pendiente')}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{o.description}</p>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Offer dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle>{language === 'en' ? 'Send your offer' : 'Envía tu oferta'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{language === 'en' ? 'Price (USD)' : 'Precio (USD)'} *</Label>
              <Input
                data-testid="offer-price"
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>

            <div>
              <Label>{language === 'en' ? 'Description' : 'Descripción'} *</Label>
              <Textarea
                data-testid="offer-desc"
                rows={3}
                placeholder={language === 'en' ? 'Describe what you offer…' : 'Describe lo que ofreces…'}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            {/* Image uploader */}
            <div>
              <Label className="flex items-center justify-between">
                <span>{language === 'en' ? 'Photos (up to 3)' : 'Fotos (hasta 3)'}</span>
                <span className="text-[10px] text-muted-foreground font-normal">
                  {form.images.length}/3
                </span>
              </Label>

              {/* Upload progress banner */}
              {uploading && uploadProgress.total > 0 && (
                <div
                  data-testid="upload-progress-banner"
                  className="mt-2 rounded-xl bg-caribbean/10 border border-caribbean/30 p-2.5 flex items-center gap-2"
                >
                  <Loader2 className="animate-spin text-caribbean shrink-0" size={16} />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-caribbean">
                      {language === 'en'
                        ? `Uploading photo ${uploadProgress.current}/${uploadProgress.total}…`
                        : `Subiendo foto ${uploadProgress.current}/${uploadProgress.total}…`}
                    </p>
                    <div className="mt-1 h-1 rounded-full bg-caribbean/20 overflow-hidden">
                      <div
                        className="h-full bg-caribbean transition-all duration-300"
                        style={{
                          width: `${Math.round((uploadProgress.current / Math.max(1, uploadProgress.total)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-2 grid grid-cols-3 gap-2">
                {form.images.map((img, idx) => (
                  <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-border bg-muted">
                    <img src={photoSrc(img)} alt={`offer-${idx}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      data-testid={`remove-image-${idx}`}
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-destructive"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}

                {/* Pending skeleton tiles while uploading */}
                {uploading && Array.from({ length: Math.max(0, uploadProgress.total - uploadProgress.current) }).map((_, i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="aspect-square rounded-xl border border-border bg-muted flex items-center justify-center animate-pulse"
                  >
                    <Loader2 className="animate-spin text-caribbean" size={18} />
                  </div>
                ))}

                {form.images.length < 3 && !uploading && (
                  <label
                    data-testid="offer-add-photo"
                    className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <Camera size={18} />
                    <span className="text-[10px]">{language === 'en' ? 'Add' : 'Añadir'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </label>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {language === 'en'
                  ? 'Tip: photos are compressed and uploaded as you pick them.'
                  : 'Tip: las fotos se comprimen y suben al seleccionarlas.'}
              </p>
            </div>

            {/* Includes with live preview */}
            <div>
              <Label>{language === 'en' ? 'Includes (one per line)' : 'Incluye (uno por línea)'}</Label>
              <Textarea
                data-testid="offer-includes"
                rows={3}
                placeholder={language === 'en'
                  ? 'Pickup at hotel\nDrinks & snacks\nProfessional guide'
                  : 'Recogida en hotel\nBebidas y snacks\nGuía profesional'}
                value={form.includes}
                onChange={(e) => setForm({ ...form, includes: e.target.value })}
              />
              {includesPreview.length > 0 && (
                <div className="mt-2 rounded-xl bg-caribbean/5 border border-caribbean/20 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-caribbean font-bold mb-1.5">
                    {language === 'en' ? 'Preview' : 'Vista previa'}
                  </p>
                  <ul className="space-y-1">
                    {includesPreview.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs">
                        <Check size={13} className="text-caribbean shrink-0 mt-0.5" />
                        <span className="leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <Label className="flex items-center gap-1.5">
                <StickyNote size={13} className="text-gold" />
                {language === 'en' ? 'Note (optional)' : 'Nota (opcional)'}
              </Label>
              <Textarea
                data-testid="offer-note"
                rows={3}
                placeholder={language === 'en'
                  ? 'Anything else the tourist should know…'
                  : 'Algo más que el turista deba saber…'}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>

            <Button
              data-testid="offer-submit"
              onClick={submitOffer}
              disabled={submitting || uploading}
              className="w-full h-11 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
            >
              {submitting
                ? <Loader2 className="animate-spin" size={16} />
                : (language === 'en' ? 'Send offer' : 'Enviar oferta')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </MobileShell>
  );
}
